<script setup>
import { computed } from 'vue';
import { getI18nKey } from 'dashboard/routes/dashboard/settings/helper/settingsHelper';
import ShowMore from 'dashboard/components/widgets/ShowMore.vue';
import { useI18n } from 'vue-i18n';
import { BaseTableRow, BaseTableCell } from 'dashboard/components-next/table';
import Button from 'dashboard/components-next/button/Button.vue';
import { formatRelativeTime } from 'dashboard/helper/relativeTime';

const props = defineProps({
  webhook: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  isTesting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['edit', 'delete', 'test']);
const { t, locale } = useI18n();

// Última entrega registrada (ok ou erro com HTTP): mostra a quebra antes de o
// cliente reclamar. Sem registro, a linha diz isso em vez de ficar muda.
const lastDelivery = computed(() => props.webhook.last_delivery || null);
const lastDeliveryText = computed(() => {
  const delivery = lastDelivery.value;
  if (!delivery) return t('INTEGRATION_SETTINGS.WEBHOOK.LAST_DELIVERY.NONE');
  const at = delivery.at ? new Date(delivery.at) : null;
  const time =
    at && !Number.isNaN(at.getTime())
      ? formatRelativeTime(at, { locale: locale?.value })
      : '';
  if (delivery.ok) {
    return t('INTEGRATION_SETTINGS.WEBHOOK.LAST_DELIVERY.OK', { time });
  }
  if (delivery.http_status) {
    return t('INTEGRATION_SETTINGS.WEBHOOK.LAST_DELIVERY.ERROR_HTTP', {
      status: delivery.http_status,
      time,
    });
  }
  return t('INTEGRATION_SETTINGS.WEBHOOK.LAST_DELIVERY.ERROR', { time });
});
const lastDeliveryClass = computed(() => {
  if (!lastDelivery.value) return 'text-n-slate-11';
  return lastDelivery.value.ok ? 'text-n-teal-11' : 'text-n-ruby-11';
});
const subscribedEvents = computed(() => {
  const { subscriptions } = props.webhook;
  return subscriptions
    .map(event =>
      t(
        getI18nKey(
          'INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.EVENTS',
          event
        )
      )
    )
    .join(', ');
});
</script>

<template>
  <BaseTableRow :item="webhook">
    <template #default>
      <BaseTableCell>
        <div class="flex gap-2 font-medium break-words text-n-slate-12">
          <template v-if="webhook.name">
            {{ webhook.name }}
            <span class="text-n-slate-11">
              {{ webhook.url }}
            </span>
          </template>
          <template v-else>
            {{ webhook.url }}
          </template>
        </div>
        <div class="block mt-1 text-sm text-n-slate-11">
          <span class="font-medium">
            {{ $t('INTEGRATION_SETTINGS.WEBHOOK.SUBSCRIBED_EVENTS') }}:
          </span>
          <ShowMore :text="subscribedEvents" :limit="60" />
        </div>
        <div
          class="mt-1 text-xs"
          :class="lastDeliveryClass"
          :title="lastDelivery?.error || undefined"
          data-testid="webhook-last-delivery"
        >
          {{ lastDeliveryText }}
        </div>
      </BaseTableCell>

      <BaseTableCell align="end" class="w-36">
        <div class="flex justify-end gap-3 flex-shrink-0">
          <Button
            v-tooltip.top="$t('INTEGRATION_SETTINGS.WEBHOOK.TEST.BUTTON_TEXT')"
            icon="i-ph-paper-plane-tilt"
            slate
            sm
            :is-loading="isTesting"
            :aria-label="$t('INTEGRATION_SETTINGS.WEBHOOK.TEST.BUTTON_TEXT')"
            data-testid="webhook-test-button"
            @click="emit('test', webhook)"
          />
          <Button
            v-tooltip.top="$t('INTEGRATION_SETTINGS.WEBHOOK.EDIT.BUTTON_TEXT')"
            :aria-label="$t('INTEGRATION_SETTINGS.WEBHOOK.EDIT.BUTTON_TEXT')"
            icon="i-woot-edit-pen"
            slate
            sm
            @click="emit('edit', webhook)"
          />
          <Button
            v-tooltip.top="
              $t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.BUTTON_TEXT')
            "
            :aria-label="$t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.BUTTON_TEXT')"
            icon="i-woot-bin"
            slate
            sm
            class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
            @click="emit('delete', webhook, index)"
          />
        </div>
      </BaseTableCell>
    </template>
  </BaseTableRow>
</template>
